import { useMemo } from "react";
import { Message } from "@/types/index";
import { WebSocketMessage } from "@/types/websocket";

interface UseUniqueMessagesOptions {
  /**
   * Campo usado para identificar mensagens únicas
   * @default 'id'
   */
  uniqueKey?: keyof Message | keyof WebSocketMessage;

  /**
   * Função customizada para comparar mensagens
   * @param message1 Primeira mensagem
   * @param message2 Segunda mensagem
   * @returns true se as mensagens são iguais
   */
  compareFn?: (
    message1: Message | WebSocketMessage,
    message2: Message | WebSocketMessage,
  ) => boolean;

  /**
   * Ordenar mensagens após remover duplicatas
   * @default true
   */
  sort?: boolean;

  /**
   * Campo usado para ordenação
   * @default 'timestamp'
   */
  sortKey?: keyof Message | keyof WebSocketMessage;

  /**
   * Ordem de classificação
   * @default 'asc'
   */
  sortOrder?: "asc" | "desc";
}

/**
 * Hook para gerenciar mensagens únicas com memoização
 *
 * @param messages Array de mensagens
 * @param options Opções de configuração
 * @returns Array de mensagens únicas
 *
 * @example
 * ```typescript
 * const uniqueMessages = useUniqueMessages(chatMessages, {
 *   uniqueKey: 'id',
 *   sort: true,
 *   sortKey: 'timestamp',
 *   sortOrder: 'asc'
 * });
 * ```
 */
export const useUniqueMessages = <T extends Message | WebSocketMessage>(
  messages: T[],
  options: UseUniqueMessagesOptions = {},
): T[] => {
  const {
    uniqueKey = "id",
    compareFn,
    sort = true,
    sortKey = "timestamp",
    sortOrder = "asc",
  } = options;

  return useMemo(() => {
    if (!messages || messages.length === 0) {
      return [];
    }

    // Remove duplicatas
    let uniqueMessages: T[];

    if (compareFn) {
      // Usa função de comparação customizada
      uniqueMessages = messages.filter(
        (message, index, self) =>
          index === self.findIndex((m) => compareFn(message, m)),
      );
    } else {
      // Usa campo único padrão
      uniqueMessages = messages.filter(
        (message, index, self) =>
          index === self.findIndex((m) => m[uniqueKey] === message[uniqueKey]),
      );
    }

    // Ordena mensagens se solicitado
    if (sort && sortKey) {
      uniqueMessages.sort((a, b) => {
        const aValue = a[sortKey];
        const bValue = b[sortKey];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortOrder === "asc" ? -1 : 1;
        if (bValue == null) return sortOrder === "asc" ? 1 : -1;

        // Trata timestamps especificamente
        if (sortKey === "timestamp") {
          const aTime =
            typeof aValue === "string"
              ? new Date(aValue).getTime()
              : Number(aValue);
          const bTime =
            typeof bValue === "string"
              ? new Date(bValue).getTime()
              : Number(bValue);

          if (sortOrder === "asc") {
            return aTime - bTime;
          } else {
            return bTime - aTime;
          }
        }

        // Comparação genérica
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return uniqueMessages;
  }, [messages, uniqueKey, compareFn, sort, sortKey, sortOrder]);
};

/**
 * Hook especializado para mensagens de chat
 *
 * @param messages Array de mensagens de chat
 * @returns Array de mensagens únicas ordenadas por timestamp
 */
export const useUniqueChatMessages = (messages: Message[]): Message[] => {
  return useUniqueMessages(messages, {
    uniqueKey: "id",
    sort: true,
    sortKey: "timestamp",
    sortOrder: "asc",
  });
};

/**
 * Hook especializado para mensagens WebSocket
 *
 * @param messages Array de mensagens WebSocket
 * @returns Array de mensagens únicas ordenadas por timestamp
 */
export const useUniqueWebSocketMessages = (
  messages: WebSocketMessage[],
): WebSocketMessage[] => {
  return useUniqueMessages(messages, {
    uniqueKey: "id",
    sort: true,
    sortKey: "timestamp",
    sortOrder: "asc",
  });
};
